import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { KeyRound, Loader2, AlertTriangle, Mail } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { apiService, AccesUpSpotFiche, CompteUpSpot, ReferentielUpSpot } from '@/services/api'
import { EditeurDroitsUpSpot, ResumeDroitsUpSpot, peutGererSelonRole } from '@/components/DroitsUpSpot'
import ChoixSuppressionUpSpot, { type ChoixCompteUpSpot } from '@/components/ChoixSuppressionUpSpot'

/* La zone « Accès UP-SPOT » d'une fiche contact (24/09/2026).
 *
 * UP-CRM est la porte d'entrée des comptes UP-SPOT (décision d'Arnaud du
 * 24/09/2026). Tout le monde voit ici le compte de la personne, lu en direct
 * dans UP-SPOT ; seuls les administrateurs d'UP-CRM le gèrent : ouvrir un
 * compte par invitation, régler le rôle, le service et les droits, désactiver.
 *
 * Donner ou retirer le rôle Admin demande une confirmation explicite : il
 * donne tous les droits sur UP-SPOT.
 *
 * Depuis le 01/10/2026, la gestion se fait dans la fenêtre « Modifier » du
 * contact (prop `gestion`), dans une section à part qui a son propre bouton
 * d'enregistrement ; la fiche en lecture n'affiche plus qu'un résumé.
 */

interface Props {
  contactId: number
  /** Adresse de la fiche : sans elle, pas de compte possible. */
  email?: string | null
  /** Appelé après toute modification, pour rafraîchir la liste. */
  onChange?: () => void
  /** Vrai dans la fenêtre « Modifier » : les administrateurs y gèrent l'accès. */
  gestion?: boolean
  /** Hors de la page Contacts : adresse de la fenêtre « Modifier » du contact. */
  lienGestion?: string
}

type Mode = 'lecture' | 'edition' | 'creation'

interface Formulaire {
  idRole: number | null
  departementId: number | null
  acces: number[]
  gestion: number[]
}

const formulaireVide: Formulaire = { idRole: null, departementId: null, acces: [], gestion: [] }

function dateEtHeure(valeur: string | null | undefined): string {
  if (!valeur) return 'jamais'
  return new Date(valeur).toLocaleString('fr-FR', {
    timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

const memes = (a: number[], b: number[]) =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join()

export function UpSpotPanel({ contactId, email, onChange, gestion = false, lienGestion }: Props) {
  const { toast } = useToast()
  const [acces, setAcces] = useState<AccesUpSpotFiche | null>(null)
  const [referentiel, setReferentiel] = useState<ReferentielUpSpot | null>(null)
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>('lecture')
  const [form, setForm] = useState<Formulaire>(formulaireVide)
  const [envoi, setEnvoi] = useState(false)
  /* Le message de confirmation du rôle Admin, quand le serveur l'exige. */
  const [confirmationAdmin, setConfirmationAdmin] = useState<string | null>(null)
  /* Le choix « Désactiver / Tout supprimer » ouvert par « Désactiver le
     compte » dans « Modifier » (02/10/2026). */
  const [choixCompteOuvert, setChoixCompteOuvert] = useState(false)
  const [choixCompte, setChoixCompte] = useState<ChoixCompteUpSpot>('desactiver')
  const [suppressionConfirmee, setSuppressionConfirmee] = useState(false)
  /* Un compte existe déjà dans UP-SPOT à cette adresse, sans fiche. */
  const [compteExistant, setCompteExistant] = useState<number | null>(null)

  const charger = useCallback(async () => {
    setChargement(true)
    setErreur(null)
    const r = await apiService.accesUpSpot(contactId)
    if (r.success && r.data) {
      setAcces(r.data)
      if (r.data.configure && !referentiel) {
        const ref = await apiService.referentielUpSpot()
        if (ref.success && ref.data) setReferentiel(ref.data)
      }
    } else {
      setErreur(r.error || "L'accès UP-SPOT n'a pas pu être lu.")
    }
    setChargement(false)
  }, [contactId, referentiel])

  useEffect(() => {
    setMode('lecture')
    setConfirmationAdmin(null)
    setChoixCompteOuvert(false)
    setCompteExistant(null)
    void charger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId])

  /* Dans « Modifier », un compte existant s'ouvre directement en édition :
     pas de clic de plus pour atteindre les droits. */
  useEffect(() => {
    if (gestion && acces?.peutGerer && acces.lien && acces.compte && mode === 'lecture') {
      ouvrirEdition(acces.compte)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acces, gestion])

  const apres = async (message: string) => {
    toast({ title: message })
    setMode('lecture')
    setConfirmationAdmin(null)
    setChoixCompteOuvert(false)
    await charger()
    onChange?.()
  }

  const nomDuRole = (idRole: number | null) =>
    referentiel?.roles.find((r) => r.id === idRole)?.nom ?? null

  const ouvrirCreation = () => {
    const utilisateur = referentiel?.roles.find((r) => r.nom.toLowerCase() === 'utilisateur')
    setForm({ ...formulaireVide, idRole: utilisateur?.id ?? referentiel?.roles[0]?.id ?? null })
    setCompteExistant(null)
    setMode('creation')
  }

  const ouvrirEdition = (compte: CompteUpSpot) => {
    setForm({
      idRole: compte.idRole,
      departementId: compte.departementId,
      acces: compte.droits.acces,
      gestion: compte.droits.gestion,
    })
    setMode('edition')
  }

  const creer = async (confirme = false) => {
    if (!form.idRole) return
    setEnvoi(true)
    const r = await apiService.creerCompteUpSpot(contactId, {
      idRole: form.idRole,
      departementId: form.departementId,
      droits: { acces: form.acces, gestion: peutGererSelonRole(nomDuRole(form.idRole)) ? form.gestion : [] },
      confirmationAdmin: confirme,
    })
    setEnvoi(false)
    if (r.success && r.data) {
      await apres(
        r.data.invitationEnvoyee
          ? `Compte créé. L'invitation a été envoyée à ${r.data.email}.`
          : "Compte créé, mais l'invitation n'a pas pu partir : utilisez « Renvoyer l'invitation ».",
      )
      return
    }
    if (r.code === 'CONFIRMATION_ADMIN') { setConfirmationAdmin(r.error ?? ''); return }
    if (r.code === 'COMPTE_EXISTANT' && r.compteId) { setCompteExistant(r.compteId); return }
    toast({ title: 'Compte non créé', description: r.error, variant: 'destructive' })
  }

  const enregistrer = async (compte: CompteUpSpot, confirme = false) => {
    setEnvoi(true)
    const modif: { idRole?: number; departementId?: number | null; confirmationAdmin?: boolean } = {}
    if (form.idRole && form.idRole !== compte.idRole) modif.idRole = form.idRole
    if (form.departementId !== compte.departementId) modif.departementId = form.departementId
    if (Object.keys(modif).length > 0) {
      const r = await apiService.modifierCompteUpSpot(contactId, { ...modif, confirmationAdmin: confirme })
      if (!r.success) {
        setEnvoi(false)
        if (r.code === 'CONFIRMATION_ADMIN') { setConfirmationAdmin(r.error ?? ''); return }
        toast({ title: 'Accès non modifié', description: r.error, variant: 'destructive' })
        return
      }
    }
    const gestion = peutGererSelonRole(nomDuRole(form.idRole)) ? form.gestion : []
    if (!memes(form.acces, compte.droits.acces) || !memes(gestion, compte.droits.gestion)) {
      const r = await apiService.droitsUpSpot(contactId, { acces: form.acces, gestion })
      if (!r.success) {
        setEnvoi(false)
        toast({ title: 'Droits non enregistrés', description: r.error, variant: 'destructive' })
        return
      }
    }
    setEnvoi(false)
    await apres('Accès UP-SPOT enregistré.')
  }

  const changerStatut = async (statut: 'actif' | 'desactive') => {
    setEnvoi(true)
    const r = await apiService.modifierCompteUpSpot(contactId, { statut })
    setEnvoi(false)
    if (r.success) {
      await apres(statut === 'desactive'
        ? 'Compte désactivé : la personne ne peut plus se connecter à UP-SPOT.'
        : 'Compte réactivé : la personne peut de nouveau se connecter.')
    } else {
      toast({ title: 'Statut non modifié', description: r.error, variant: 'destructive' })
    }
  }

  const ouvrirChoixCompte = (choix: ChoixCompteUpSpot) => {
    setChoixCompte(choix)
    setSuppressionConfirmee(false)
    setChoixCompteOuvert(true)
  }

  const appliquerChoixCompte = async () => {
    if (choixCompte === 'desactiver') {
      await changerStatut('desactive')
      return
    }
    setEnvoi(true)
    const r = await apiService.supprimerCompteUpSpot(contactId)
    setEnvoi(false)
    if (r.success) {
      await apres('Le compte UP-SPOT et ses réservations ont été supprimés. La fiche est conservée.')
    } else {
      toast({ title: 'Compte non supprimé', description: r.error, variant: 'destructive' })
    }
  }

  const renvoyerInvitation = async () => {
    setEnvoi(true)
    const r = await apiService.renvoyerInvitationUpSpot(contactId)
    setEnvoi(false)
    if (r.success && r.data?.invitationEnvoyee) toast({ title: "L'invitation a été renvoyée. Le nouveau lien est valable 24 heures." })
    else toast({ title: "L'invitation n'a pas pu être renvoyée", description: r.error, variant: 'destructive' })
  }

  const rattacher = async (compteId: number) => {
    setEnvoi(true)
    const r = await apiService.rattacherCompteUpSpot(compteId, contactId)
    setEnvoi(false)
    if (r.success) await apres('Le compte UP-SPOT est maintenant rattaché à cette fiche.')
    else toast({ title: 'Rattachement impossible', description: r.error, variant: 'destructive' })
  }

  // --- Rendu ------------------------------------------------------------------

  /* Dans « Modifier » (02/10/2026), un bandeau violet foncé marque la zone
     UP-SPOT : ses boutons agissent tout de suite, à part du reste de la
     fenêtre. Sur la fiche en lecture, l'en-tête reste clair. */
  const cadre = (contenu: ReactNode) => (
    <div className={`rounded-2xl border overflow-hidden ${gestion ? 'border-violet-300 bg-violet-50/60' : 'border-violet-100 bg-violet-50/50'}`}>
      <div className={`flex items-center gap-2 px-4 py-2.5 ${gestion ? 'bg-violet-700' : 'bg-violet-100/70 border-b border-violet-100'}`}>
        <KeyRound className={`w-4 h-4 ${gestion ? 'text-white' : 'text-violet-700'}`} />
        <h3 className={`text-xs font-bold uppercase tracking-wide ${gestion ? 'text-white' : 'text-violet-800'}`}>Accès UP-SPOT</h3>
      </div>
      <div className="px-4 py-3 space-y-3">{contenu}</div>
    </div>
  )
  const mentionZone = (
    <p className="text-xs text-violet-800">
      Ces boutons agissent directement sur UP-SPOT, indépendamment de « Modifier le contact ».
    </p>
  )

  /* Les gestes de gestion n'apparaissent que dans « Modifier ». */
  const peutGererIci = Boolean(acces?.peutGerer && gestion)
  const renvoiVersModifier = acces?.peutGerer && !gestion && (
    lienGestion ? (
      <Link to={lienGestion} className="inline-block text-xs font-semibold text-violet-700 hover:underline">
        Gérer l'accès UP-SPOT dans la fenêtre « Modifier » du contact
      </Link>
    ) : (
      <p className="text-xs text-slate-500">Pour gérer l'accès UP-SPOT, ouvrez « Modifier » sur la fiche.</p>
    )
  )

  if (chargement && !acces) {
    return cadre(<div className="h-4 bg-violet-200/60 rounded animate-pulse w-40" />)
  }
  if (erreur) return cadre(<p className="text-sm text-red-600">{erreur}</p>)
  if (!acces) return null
  if (!acces.configure) {
    return cadre(<p className="text-sm text-slate-500">UP-SPOT n'est pas encore relié à UP-CRM.</p>)
  }

  const blocConfirmationAdmin = (confirmer: () => void) => confirmationAdmin !== null && (
    <div className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-2">
      <p className="flex items-start gap-2 text-sm text-red-800">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          {confirmationAdmin || 'Ce changement concerne le rôle Admin.'} Un administrateur d'UP-SPOT peut gérer toutes
          les ressources et toutes les réservations. Confirmez-vous ?
        </span>
      </p>
      <div className="flex gap-2">
        <Button size="sm" className="bg-red-600 hover:bg-red-700" disabled={envoi} onClick={confirmer}>
          Oui, je confirme
        </Button>
        <Button size="sm" variant="outline" disabled={envoi} onClick={() => setConfirmationAdmin(null)}>Annuler</Button>
      </div>
    </div>
  )

  const champsCompte = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <label className="text-sm">
        <span className="block text-xs text-slate-500 mb-1">Rôle</span>
        <select
          className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
          value={form.idRole ?? ''}
          onChange={(e) => { setForm({ ...form, idRole: Number(e.target.value) }); setConfirmationAdmin(null) }}
        >
          {referentiel?.roles.map((r) => <option key={r.id} value={r.id}>{r.nom}</option>)}
        </select>
      </label>
      <label className="text-sm">
        <span className="block text-xs text-slate-500 mb-1">Service</span>
        <select
          className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
          value={form.departementId ?? ''}
          onChange={(e) => setForm({ ...form, departementId: e.target.value ? Number(e.target.value) : null })}
        >
          <option value="">Aucun service</option>
          {referentiel?.services.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
        </select>
      </label>
    </div>
  )

  const editeurDroits = referentiel && (
    <div>
      <p className="text-xs font-semibold text-slate-600 mb-2">Ressources</p>
      <EditeurDroitsUpSpot
        referentiel={referentiel}
        acces={form.acces}
        gestion={form.gestion}
        role={nomDuRole(form.idRole)}
        onChange={(a, g) => setForm({ ...form, acces: a, gestion: g })}
      />
    </div>
  )

  // --- La fiche porte un compte -------------------------------------------------
  if (acces.lien) {
    const compte = acces.compte
    if (!compte) {
      return cadre(<p className="text-sm text-red-600">{acces.erreurCompte || 'Le compte UP-SPOT est illisible pour le moment.'}</p>)
    }
    const adresseDifferente = (email || '').trim().toLowerCase() !== compte.email.trim().toLowerCase()

    const etat = (
      <div className="flex flex-wrap items-center gap-2">
        {compte.statut === 'desactive' ? (
          <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600">Désactivé</span>
        ) : (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Actif</span>
        )}
        {compte.invitationEnAttente && compte.statut !== 'desactive' && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
            Invitation pas encore acceptée
          </span>
        )}
      </div>
    )
    const lignes = (liste: [string, ReactNode][]) => (
      <div className="space-y-1 text-sm">
        {liste.map(([libelle, valeur]) => (
          <p key={libelle} className="flex gap-2">
            <span className="text-xs text-slate-400 w-24 shrink-0 pt-0.5">{libelle}</span>
            <span className="flex-1 min-w-0 text-slate-800">{valeur}</span>
          </p>
        ))}
      </div>
    )
    const avertissementAdresse = adresseDifferente && (
      <p className="text-xs text-amber-700">
        L'adresse de connexion à UP-SPOT n'est pas celle de la fiche. Elle ne change que si l'on modifie
        l'adresse de la fiche : corriger le nom ne la touche pas.
      </p>
    )

    // Dans « Modifier », pour un administrateur : tout se règle ici.
    if (mode === 'edition' && peutGererIci) {
      const modifie =
        form.idRole !== compte.idRole ||
        form.departementId !== compte.departementId ||
        !memes(form.acces, compte.droits.acces) ||
        !memes(peutGererSelonRole(nomDuRole(form.idRole)) ? form.gestion : [], compte.droits.gestion)
      return cadre(
        <>
          {etat}
          {lignes([
            ['Connexion', <span className="break-all">{compte.email}</span>],
            ['Dernière visite', dateEtHeure(compte.derniereConnexion)],
          ])}
          {avertissementAdresse}
          {champsCompte}
          {editeurDroits}
          {blocConfirmationAdmin(() => void enregistrer(compte, true))}
          {/* Une seule rangée (02/10/2026) : enregistrer, annuler, puis le
              sort du compte. */}
          {confirmationAdmin === null && !choixCompteOuvert && (
            <div className="flex flex-wrap items-center gap-1.5">
              <Button size="sm" type="button" className="h-8 px-2.5 text-xs" disabled={envoi || !modifie} onClick={() => void enregistrer(compte)}>
                {envoi && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Enregistrer
              </Button>
              <Button size="sm" type="button" variant="outline" className="h-8 px-2.5 text-xs" disabled={envoi || !modifie} onClick={() => ouvrirEdition(compte)}>
                Annuler
              </Button>
              {compte.invitationEnAttente && compte.statut !== 'desactive' && (
                <Button size="sm" type="button" variant="outline" className="h-8 px-2.5 text-xs" disabled={envoi} onClick={() => void renvoyerInvitation()}>
                  <Mail className="w-3.5 h-3.5 mr-1" />Renvoyer l'invitation
                </Button>
              )}
              {compte.statut === 'desactive' ? (
                <>
                  <Button size="sm" type="button" variant="outline" className="h-8 px-2.5 text-xs" disabled={envoi} onClick={() => void changerStatut('actif')}>
                    Réactiver le compte
                  </Button>
                  <Button size="sm" type="button" variant="outline" className="h-8 px-2.5 text-xs text-red-600" disabled={envoi} onClick={() => ouvrirChoixCompte('supprimer')}>
                    Supprimer le compte
                  </Button>
                </>
              ) : (
                <Button size="sm" type="button" variant="outline" className="h-8 px-2.5 text-xs text-red-600" disabled={envoi} onClick={() => ouvrirChoixCompte('desactiver')}>
                  Désactiver le compte
                </Button>
              )}
            </div>
          )}
          {choixCompteOuvert && (
            <div className="rounded-xl border border-red-200 bg-white p-3 space-y-3">
              <ChoixSuppressionUpSpot
                contactId={contactId}
                ficheConservee
                choix={choixCompte}
                onChoix={setChoixCompte}
                confirme={suppressionConfirmee}
                onConfirme={setSuppressionConfirmee}
              />
              {choixCompte === 'desactiver' && compte.statut !== 'desactive' && (
                <p className="text-xs text-gray-600">
                  Sa session en cours sera coupée immédiatement. On pourra réactiver le compte à tout moment.
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  type="button"
                  className="bg-red-600 hover:bg-red-700"
                  disabled={
                    envoi ||
                    (choixCompte === 'supprimer' && !suppressionConfirmee) ||
                    (choixCompte === 'desactiver' && compte.statut === 'desactive')
                  }
                  onClick={() => void appliquerChoixCompte()}
                >
                  {envoi && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                  {choixCompte === 'supprimer' ? 'Supprimer le compte et ses réservations' : 'Désactiver le compte'}
                </Button>
                <Button size="sm" type="button" variant="outline" disabled={envoi} onClick={() => setChoixCompteOuvert(false)}>Annuler</Button>
              </div>
            </div>
          )}
          {mentionZone}
        </>,
      )
    }

    // Le résumé : sur la fiche en lecture, et pour qui ne gère pas.
    return cadre(
      <>
        {etat}
        {lignes([
          ['Rôle', <span className="font-semibold">{compte.role}</span>],
          ['Service', compte.service || 'aucun'],
          ['Connexion', <span className="break-all">{compte.email}</span>],
          ['Dernière visite', dateEtHeure(compte.derniereConnexion)],
        ])}
        {avertissementAdresse}
        <ResumeDroitsUpSpot referentiel={referentiel} acces={compte.droits.acces} gestion={compte.droits.gestion} role={compte.role} />
        {acces.lien.creeParSynchro && (
          <p className="text-xs text-slate-500">Cette fiche a été créée automatiquement à partir du compte UP-SPOT.</p>
        )}
        {renvoiVersModifier}
      </>,
    )
  }

  // --- Le compte de cette adresse est porté par une autre fiche -----------------
  if (acces.porteParAutreFiche) {
    const f = acces.porteParAutreFiche
    return cadre(
      <p className="text-sm text-slate-600">
        Le compte UP-SPOT de cette adresse est porté par une autre fiche : <strong>{f.prenom} {f.nom}</strong>.
      </p>,
    )
  }

  // --- Un compte attend d'être rattaché ------------------------------------------
  if (acces.compteARattacher) {
    const c = acces.compteARattacher
    return cadre(
      <>
        <p className="text-sm text-slate-600">
          Un compte UP-SPOT existe à cette adresse ({c.prenom} {c.nom}), mais plusieurs fiches la partagent : il
          attend qu'on choisisse celle qui le porte.
        </p>
        {peutGererIci && (
          <Button size="sm" type="button" variant="outline" disabled={envoi} onClick={() => void rattacher(c.id)}>
            Rattacher le compte à cette fiche
          </Button>
        )}
        {renvoiVersModifier}
      </>,
    )
  }

  // --- Pas de compte --------------------------------------------------------------
  if (mode === 'creation' && peutGererIci) {
    return cadre(
      <>
        <p className="text-sm text-slate-600">
          Une invitation partira à <strong>{email}</strong> : la personne y choisira son mot de passe. Le lien est
          valable 24 heures.
        </p>
        {champsCompte}
        {editeurDroits}
        {compteExistant !== null && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
            <p className="text-sm text-amber-800">
              Un compte UP-SPOT existe déjà à cette adresse, sans être rattaché à une fiche. Rattachez-le plutôt que
              d'en créer un second.
            </p>
            <Button size="sm" type="button" variant="outline" disabled={envoi} onClick={() => void rattacher(compteExistant)}>
              Rattacher ce compte à cette fiche
            </Button>
          </div>
        )}
        {blocConfirmationAdmin(() => void creer(true))}
        {confirmationAdmin === null && compteExistant === null && (
          <div className="flex gap-2">
            <Button size="sm" type="button" disabled={envoi || !form.idRole} onClick={() => void creer()}>
              {envoi && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Créer le compte et envoyer l'invitation
            </Button>
            <Button size="sm" type="button" variant="outline" disabled={envoi} onClick={() => setMode('lecture')}>Annuler</Button>
          </div>
        )}
        {mentionZone}
      </>,
    )
  }

  const adresseValide = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim())
  return cadre(
    <>
      <p className="text-sm text-slate-600">Cette personne n'a pas de compte UP-SPOT.</p>
      {peutGererIci && (
        adresseValide ? (
          <Button size="sm" type="button" variant="outline" disabled={!referentiel} onClick={ouvrirCreation}>
            Ouvrir un compte UP-SPOT
          </Button>
        ) : (
          <p className="text-xs text-slate-500">
            Pour ouvrir un compte, ajoutez d'abord une adresse email à la fiche, puis enregistrez-la : elle servira à
            se connecter.
          </p>
        )
      )}
      {renvoiVersModifier}
    </>,
  )
}
